import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { executeReport } from "../api/server";
import {
  getRoles,
  getGenders,
  getEmployee_types,
  getClientCategories,
  getEmployeeCareDemands,
  getServiceTypes,
  getRevahaAuthorities,
  getPatient_status,
  getPermissions,
  getBanks,
  getFamily_status,
  getHealth_funds,
  getCountries,
  getReligions,
  getCertifications,
  getEducation,
  getPatientCategories,
  getDepartments,
  getRelativeTypes,
  getAbsenceReasons,
  getBranchGroups,
  getClientTariffs,
  getEmployeeTariffs,
  getRefundReasons,
  getLanguages,
  getHospitalizationInstitutions,
  getProfessionalRoles,
  getCourses,
  getMetaSystemTable,
  getAssetCategories,
  getAssetSubCategories,
  getAssetStatuses,
  getMetaTable,
} from "../api/server";

import Auth from "../api/auth";
import { useQuery } from "react-query";

export function useProfile() {
  return useSelector((state) => state.user.profile);
}

export function useCollectionItems({ collectionName, fetcher, param }) {
  const source = useSelector((state) => state.sources[collectionName]);
  const userProfile = useProfile();
  const { data } = useQuery(
    [source, collectionName, fetcher],
    async () => {
      const result = await fetcher(param);
      return result.data.items;
    },
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 60 * 24, //24 hours
      enabled: userProfile != null,
    }
  );
  return data || [];
}

export function useAuthToken() {
  const [token, setToken] = useState(null);
  const userProfile = useProfile();
  useEffect(() => {
    Auth.getToken().then(setToken);
  }, [userProfile]);

  return token;
}

export function useReportQuery(reportID, params) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const queryParams = useSelector((state) => state.report.queryParams);
  const filters = useSelector((state) => state.report.runTimeFilters);
  const scopePrefix = useSelector((state) => state.user.scopePrefix);
  // const filters = executeDynamicFilters
  //   .filter((i) => i.values && i.values.length > 0)
  //   .map((j) => ({ member: j.member, operator: j.operator, values: j.values }));
  // const values = filters.map((k) => k.values);
  // console.log(executeDynamicFilters);
  // console.log(values);
  useEffect(() => {
    if (reportID) {
      fetchReport(reportID);
    }
  }, [scopePrefix, reportID, queryParams, filters]);

  const fetchReport = () => {
    setError(null);
    setResult(null);
    executeReport(
      reportID,
      {
        params: { ...queryParams, query_override: { filters } },
      },
      scopePrefix
    )
      .then((result) => {
        if (result?.data?.error === "Continue wait") {
          fetchReport();
        } else {
          setResult(result);
        }
      })
      .catch(setError);
  };

  return {
    loading: result == null && error == null,
    error,
    resultSet: result && result.data,
  };
}

export function useRoles() {
  const roles = useCollectionItems({
    collectionName: "roles",
    fetcher: getRoles,
  });
  return roles;
}

export function useGenders() {
  const genders = useCollectionItems({
    collectionName: "genders",
    fetcher: getGenders,
  });
  return genders;
}

export function useEmployee_types() {
  const employee_types = useCollectionItems({
    collectionName: "employee_types",
    fetcher: getEmployee_types,
  });
  return employee_types.filter((i) => i.name !== "territories_citizen");
}

export function useClientCategories() {
  const client_categories = useCollectionItems({
    collectionName: "client_categories",
    fetcher: getClientCategories,
    param: {},
  });
  return client_categories;
}

export function useEmployeeCareDemands() {
  const care_demands = useCollectionItems({
    collectionName: "care_demands",
    fetcher: getEmployeeCareDemands,
  });
  return care_demands;
}

export function useServiceTypes() {
  const service_types = useCollectionItems({
    collectionName: "service_types",
    fetcher: getServiceTypes,
  });
  return service_types;
}

export function useRevahaAuthorities() {
  const revaha_authorities = useCollectionItems({
    collectionName: "revaha_authorities",
    fetcher: getMetaSystemTable,
    param: "revaha_authorities",
  });
  return revaha_authorities;
}

export function useOrderServiceTypes() {
  const order_service_types = useCollectionItems({
    collectionName: "order_service_types",
    fetcher: getMetaSystemTable,
    param: "order_service_types",
  });
  return order_service_types;
}

export function useSalary_symbols() {
  const salary_symbols = useCollectionItems({
    collectionName: "salary_symbols",
    fetcher: getMetaSystemTable,
    param: "salary/salary_symbols",
  });
  return salary_symbols;
}

export function useSalary_column_settings() {
  const salary_column_settings = useCollectionItems({
    collectionName: "salary_column_settings",
    fetcher: getMetaSystemTable,
    param: "column_settings/salary",
  });
  return salary_column_settings;
}

export function useCrm_task_priorities() {
  const crm_task_priorities = useCollectionItems({
    collectionName: "crm_task_priorities",
    fetcher: getMetaSystemTable,
    param: "crm/crm_task_priorities",
  });
  return crm_task_priorities;
}

export function useCrm_task_statuses() {
  const crm_task_statuses = useCollectionItems({
    collectionName: "crm_task_statuses",
    fetcher: getMetaSystemTable,
    param: "crm/crm_task_statuses",
  });
  return crm_task_statuses;
}

export function usePatient_opportunity_statuses() {
  const patient_opportunity_statuses = useCollectionItems({
    collectionName: "patient_opportunity_statuses",
    fetcher: getMetaSystemTable,
    param: "crm/patient_opportunity_statuses",
  });
  return patient_opportunity_statuses;
}

export function useClient_tariffs() {
  const client_tariffs = useCollectionItems({
    collectionName: "client_tariffs",
    fetcher: getClientTariffs,
  });
  return client_tariffs;
}

export function useEmployees_tariffs() {
  const employees_tariffs = useCollectionItems({
    collectionName: "employees_tariffs",
    fetcher: getEmployeeTariffs,
  });
  return employees_tariffs;
}

export function usePatientStatus() {
  const patient_status = useCollectionItems({
    collectionName: "patient_status",
    fetcher: getPatient_status,
  });
  return patient_status;
}

export function usePermissions() {
  const permissions = useCollectionItems({
    collectionName: "permissions",
    fetcher: getPermissions,
  });
  return permissions;
}

export function useBanks() {
  const banks = useCollectionItems({
    collectionName: "banks",
    fetcher: getBanks,
  });
  return banks;
}

export function useFamilyStatus() {
  const familyStatus = useCollectionItems({
    collectionName: "familyStatus",
    fetcher: getFamily_status,
  });
  return familyStatus;
}
export function useAssetsCategories() {
  const asset_categories = useCollectionItems({
    collectionName: "asset_categories",
    fetcher: getAssetCategories,
  });
  return asset_categories;
}

export function useAssetsSubCategories() {
  const asset_sub_categories = useCollectionItems({
    collectionName: "asset_sub_categories",
    fetcher: getAssetSubCategories,
  });
  return asset_sub_categories;
}
export function useAssetsStatuses() {
  const asset_statuses = useCollectionItems({
    collectionName: "asset_statuses",
    fetcher: getAssetStatuses,
  });
  return asset_statuses;
}

export function useManaged_gap_reasons() {
  const managed_gap_reasons = useCollectionItems({
    collectionName: "managed_gap_reasons",
    fetcher: getMetaSystemTable,
    param: "managed_gap_reasons",
  });
  return managed_gap_reasons;
}

export function useSalary_export_types() {
  const salary_export_types = useCollectionItems({
    collectionName: "salary_export_types",
    fetcher: getMetaTable,
    param: "salary_export_types",
  });
  return salary_export_types;
}

const metadataFetchers = {
  client_categories: getClientCategories,
  employee_types: getEmployee_types,
  genders: getGenders,
  care_demands: getEmployeeCareDemands,
  service_types: getServiceTypes,
  revaha_authorities: getRevahaAuthorities,
  patient_status: getPatient_status,
  banks: getBanks,
  familyStatus: getFamily_status,
  healthFunds: getHealth_funds,
  countries: getCountries,
  religions: getReligions,
  education: getEducation,
  certification: getCertifications,
  patientCategories: getPatientCategories,
  relative_types: getRelativeTypes,
  departments: getDepartments,
  professional_roles: getProfessionalRoles,
};
export function useMetadata(name) {
  const items = useCollectionItems({
    collectionName: name,
    fetcher: metadataFetchers[name],
  });
  return items;
}

export function useHealth_funds() {
  const healthFunds = useCollectionItems({
    collectionName: "healthFunds",
    fetcher: getHealth_funds,
  });
  return healthFunds;
}

export function useCountries() {
  const countries = useCollectionItems({
    collectionName: "countries",
    fetcher: getCountries,
  });
  return countries;
}

export function useReligions() {
  const religions = useCollectionItems({
    collectionName: "religions",
    fetcher: getReligions,
  });
  return religions;
}

export function useEducation() {
  const education = useCollectionItems({
    collectionName: "education",
    fetcher: getEducation,
  });
  return education;
}

export function useCertification() {
  const certification = useCollectionItems({
    collectionName: "certification",
    fetcher: getCertifications,
  });
  return certification;
}

export function usePatientCategories() {
  const patient_categories = useCollectionItems({
    collectionName: "patient_categories",
    fetcher: getPatientCategories,
  });
  return patient_categories;
}

export function useRelativeTypes() {
  const relative_types = useCollectionItems({
    collectionName: "relative_types",
    fetcher: getRelativeTypes,
  });
  return relative_types;
}

export function useAbsenceReasons() {
  const absence_reasons = useCollectionItems({
    collectionName: "absence_reasons",
    fetcher: getAbsenceReasons,
  });
  return absence_reasons;
}

export function useBranchGroups() {
  const branch_groups = useCollectionItems({
    collectionName: "branch_groups",
    fetcher: getBranchGroups,
  });
  return branch_groups;
}

export function useRefundReasons() {
  const refund_reasons = useCollectionItems({
    collectionName: "refund_reasons",
    fetcher: getRefundReasons,
  });
  return refund_reasons;
}
export const useUnpaidHoursReasons = () => {
  const unpaid_hours_reasons = useCollectionItems({
    collectionName: "unpaid_hours_reasons",
    fetcher: getMetaSystemTable,
    param: "unpaid_hours_reasons",
  });
  return unpaid_hours_reasons;
};

export function useLanguages() {
  const languages = useCollectionItems({
    collectionName: "languages",
    fetcher: getLanguages,
  });
  return languages;
}

export function useHospitalizationInstitutions() {
  const hospitalization_institutions = useCollectionItems({
    collectionName: "hospitalization_institutions",
    fetcher: getHospitalizationInstitutions,
  });
  return hospitalization_institutions;
}

export function useCourses() {
  const courses = useCollectionItems({
    collectionName: "courses",
    fetcher: getCourses,
  });
  return courses;
}

export function useEndWorkReasons() {
  const end_work_period_reason = useCollectionItems({
    collectionName: "end_work_period_reason",
    fetcher: getMetaSystemTable,
    param: "end_work_period_reason",
  });
  return end_work_period_reason;
}

export function useDepratments() {
  const departments = useCollectionItems({
    collectionName: "departments",
    fetcher: getDepartments,
  });
  return departments;
}
export function useProfessionalRoles() {
  const professional_roles = useCollectionItems({
    collectionName: "professional_roles",
    fetcher: getProfessionalRoles,
  });
  return professional_roles;
}

export const useIsMount = () => {
  const isMountRef = useRef(true);
  useEffect(() => {
    isMountRef.current = false;
  }, []);
  return isMountRef.current;
};
